import ConfirmationModal from '@/components/ConfirmationModal';
import Constants from '@/Constants';
import Category from '@/models/Category';
import { ApplicationState } from '@/store';
import { activateCategoryRequest, clearCategoryManagement, inactivateCategoryRequest } from '@/store/categoryManagement/action';
import { closeModal, openModal } from '@/store/common/actions';
import React, { useEffect, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import ResultModal from '../../../../../components/ResultModal';
import CouldNotInactivateCategoryModal from '../../CouldNotInactivateCategoryModal';
import './styles.scss';

interface CategoryCardProps {
    category: Category;
    onUpdateCategoryList: () => void;
}
const CategoryCard = ({ category, onUpdateCategoryList }: CategoryCardProps) => {
    const [operation, setOperation] = useState('');
    const { strings, success, loading, error } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        success: state.categoryManagement.success,
        loading: state.categoryManagement.loading,
        error: state.categoryManagement.error,
    }));

    const dispatch = useDispatch();

    useEffect(() => {
        //Definição da mensagem do modal
        let prefix = '';
        let sufix = '';

        if (success) {
            prefix = operation == 'activate' ? strings.ACTIVATE_SUCCESS_PREFIX : strings.INACTIVATE_SUCCESS_PREFIX;
            sufix = operation == 'activate' ? strings.ACTIVATE_SUCCESS_SUFIX : strings.INACTIVATE_SUCCESS_SUFIX;
        } else {
            prefix = operation == 'activate' ? strings.ACTIVATE_FAILURE_PREFIX : strings.INACTIVATE_FAILURE_PREFIX;
            sufix = operation == 'activate' ? strings.ACTIVATE_FAILURE_SUFIX : strings.INACTIVATE_FAILURE_SUFIX;
        }
        const description = (
            <span>
                <span>{prefix + ' '}</span>
                <span>
                    <b>{category.name}</b>
                </span>
                <span>{' ' + sufix}</span>
            </span>
        );
        if (!!operation) {
            if (operation === 'inactivate' && error?.response?.status === Constants.statusCode.PRECONDITION_FAILED) {
                dispatch(closeModal(Constants.modal.ACTIVATE_INACTIVATE_RESULT_MODAL));
                dispatch(
                    openModal({
                        id: Constants.modal.COULD_NOT_INACTIVATE_CATEGORY_MODAL,
                        layoutModal: <CouldNotInactivateCategoryModal loading={loading} />,
                        isVisible: true,
                        isCloseAllowed: true,
                    }),
                );
            } else if (success || error?.response?.status !== Constants.statusCode.PRECONDITION_FAILED) {
                dispatch(
                    openModal({
                        id: Constants.modal.ACTIVATE_INACTIVATE_RESULT_MODAL,
                        layoutModal: <ResultModal description={description} loading={loading} success={success} />,
                        isVisible: true,
                        isCloseAllowed: true,
                    }),
                );
            }
            if (!loading) {
                setOperation('');
                onUpdateCategoryList();
                dispatch(clearCategoryManagement());
            }
        }
    }, [success, operation, onUpdateCategoryList, loading, error]);

    const confirmInactivateCategory = () => {
        dispatch(inactivateCategoryRequest({ id: category.id || 0 }));
        setOperation('inactivate');
    };

    const confirmActivateCategory = () => {
        dispatch(activateCategoryRequest({ id: category.id || 0 }));
        setOperation('activate');
    };

    const onInactiveCategory = () => {
        dispatch(
            openModal({
                id: Constants.modal.CONFIRMATION_MODAL,
                layoutModal: (
                    <ConfirmationModal
                        description={
                            <span>
                                <span>{strings.INACTIVATE_CATEGORY_DESCRIPTION_PREFIX + ' '}</span>
                                <span>
                                    <b>{category.name}</b>
                                </span>
                                <span>{' ' + strings.INACTIVATE_CATEGORY_DESCRIPTION_SUFIX}</span>
                            </span>
                        }
                        onConfirm={confirmInactivateCategory}
                        title={strings.INACTIVATE_CATEGORY_TITLE}
                    />
                ),
                isVisible: true,
                isCloseAllowed: true,
            }),
        );
    };

    const onActiveCategory = () => {
        dispatch(
            openModal({
                id: Constants.modal.CONFIRMATION_MODAL,
                layoutModal: (
                    <ConfirmationModal
                        description={
                            <span>
                                <span>{strings.ACTIVATE_CATEGORY_DESCRIPTION_PREFIX + ' '}</span>
                                <span>
                                    <b>{category.name}</b>
                                </span>
                                <span>{' ' + strings.ACTIVATE_CATEGORY_DESCRIPTION_SUFIX}</span>
                            </span>
                        }
                        onConfirm={confirmActivateCategory}
                        title={strings.ACTIVATE_CATEGORY_TITLE}
                    />
                ),
                isVisible: true,
                isCloseAllowed: true,
            }),
        );
    };

    const ItemMenu = () => {
        return (
            <div className="dropdown">
                <i className="material-icons-outlined align-middle" data-toggle="dropdown" style={{ color: '#808080' }}>
                    more_horiz
                </i>

                <div className="dropdown-menu dropdown-menu-right">
                    <div className="triangle-container">
                        <div className="dropdown-triangle"></div>
                        <div className="dropdown-triangle-border"></div>
                    </div>

                    {category.activated ? (
                        <div className="dropdown-item" onClick={onInactiveCategory} role="button" tabIndex={0}>
                            <i className="material-icons-outlined va-t m-right--xxs item-menu-icon">block</i>
                            {strings.INACTIVATE_ACTION}
                        </div>
                    ) : (
                        <div className="dropdown-item" onClick={onActiveCategory} role="button" tabIndex={0}>
                            <i className="material-icons-outlined va-t m-right--xxs item-menu-icon">refresh</i>
                            {strings.REACTIVATE_ACTION}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const SystemIconTooltip = () => {
        return (
            <OverlayTrigger overlay={<Tooltip id="category-system-tooltip">{strings.CATEGORY_MANAGEMENT_SYSTEM_TOOLTIP}</Tooltip>} placement="bottom">
                <i className="material-icons tooltip-system-alert">{Constants.icons.INFO_OUTLINE}</i>
            </OverlayTrigger>
        );
    };

    return (
        <li className={`category-card-item`}>
            <span className={`category-name ${!category.activated && 'inactived'}`}>{category.name}</span>
            <div>
                {!category.organizationId && (
                    <>
                        <span className="system-tag">{strings.SYSTEM}</span>

                        <SystemIconTooltip />
                    </>
                )}

                {category.organizationId && <ItemMenu />}
            </div>
        </li>
    );
};

export { CategoryCard };
